import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { DemoBadge, EmptyState, InfoNote, PriorityBadge, OPP_TYPE_LABELS } from "@/components/shared";
import { Bookmark, CheckCircle2, Loader2, Play, RefreshCw, Target, X } from "lucide-react";
import { toast } from "sonner";

const TYPES = Object.keys(OPP_TYPE_LABELS);

export default function Opportunities() {
  const { activeWebsite, activeWorkspace } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ status: "open", otype: "all", priority: "all" });
  const [approveOpen, setApproveOpen] = useState(false);
  const [confirmScope, setConfirmScope] = useState(false);
  const [confirmReview, setConfirmReview] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    if (!activeWebsite) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { status: filters.status };
      if (filters.otype !== "all") params.otype = filters.otype;
      if (filters.priority !== "all") params.priority = filters.priority;
      const { data: d } = await api.get(`/websites/${activeWebsite.id}/opportunities`, { params });
      setData(d);
      if (d.items.length && (!selected || !d.items.find((x) => x.id === selected.id))) setSelected(d.items[0]);
      if (!d.items.length) setSelected(null);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeWebsite?.id, filters]); // eslint-disable-line

  const setStatus = async (opp, status) => {
    try {
      await api.patch(`/opportunities/${opp.id}`, { status });
      toast.success(status === "dismissed" ? "Opportunity dismissed" : status === "saved" ? "Saved for later" : "Reopened");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const approveRun = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post(`/opportunities/${selected.id}/approve-run`);
      toast.success("Task approved - AI execution started");
      setApproveOpen(false);
      navigate(`/dashboard/tasks/${res.task.id}`);
    } catch (e) {
      const msg = errMsg(e);
      if (msg.includes("ai_not_connected")) {
        toast.error("Connect an AI provider to run tasks", {
          action: { label: "Connect AI", onClick: () => navigate("/dashboard/connections") },
        });
      } else {
        toast.error(msg);
      }
      setApproveOpen(false);
    } finally { setRunning(false); }
  };

  if (!activeWebsite) {
    return <EmptyState icon={Target} title="No website selected" message="Add a website to see opportunities." />;
  }

  const rec = selected?.recommendation;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Opportunities</h1>
          <p className="text-xs text-muted-foreground">Evidence-based, deterministically scored. Approve to create a scoped AI task.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="opportunities-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["open", "saved", "dismissed", "in_progress", "completed", "all"].map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.otype} onValueChange={(v) => setFilters({ ...filters, otype: v })}>
            <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="opportunities-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{OPP_TYPE_LABELS[t]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
            <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="opportunities-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {["very_high", "high", "medium", "low", "review"].map((p) => <SelectItem key={p} value={p}>{p.replaceAll("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-8" onClick={load} data-testid="opportunities-refresh-button"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>
      ) : (data?.items || []).length === 0 ? (
        <EmptyState icon={Target} title="No opportunities in this view"
          message="Run an audit and connect Search Console (or load labelled demo data) so the engine has evidence to work with."
          testId="opportunities-empty">
          <Button size="sm" variant="outline" onClick={() => navigate("/dashboard/connections")}>Open Connections</Button>
        </EmptyState>
      ) : (
        <div className="grid gap-4 xl:grid-cols-12">
          <div className="space-y-2 xl:col-span-5" data-testid="opportunities-list">
            {data.items.map((o) => (
              <button key={o.id} onClick={() => setSelected(o)}
                data-testid="opportunity-row"
                className={`w-full rounded-lg border bg-card p-3 text-left hover:bg-muted/40 ${selected?.id === o.id ? "border-primary ring-1 ring-primary/20" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <PriorityBadge priority={o.priority} />
                    <Badge variant="outline" className="h-6 px-2 text-xs">{OPP_TYPE_LABELS[o.type] || o.type}</Badge>
                    {o.is_demo && <DemoBadge />}
                  </div>
                  <span className="text-sm font-semibold tabular-nums">{o.score}</span>
                </div>
                <div className="mt-1.5 truncate text-sm font-medium">{o.recommendation?.title}</div>
                <div className="truncate font-mono text-[11px] text-muted-foreground">{o.page || o.queries?.[0] || "—"}</div>
              </button>
            ))}
          </div>

          <div className="xl:col-span-7">
            {selected && (
              <Card className="shadow-none xl:sticky xl:top-[72px]" data-testid="opportunity-detail-panel">
                <CardContent className="space-y-4 p-4 sm:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <PriorityBadge priority={selected.priority} />
                        <Badge variant="outline" className="h-6 px-2 text-xs">{OPP_TYPE_LABELS[selected.type]}</Badge>
                        {selected.is_demo && <DemoBadge />}
                        <Badge variant="outline" className="h-6 px-2 text-xs capitalize">{selected.status.replaceAll("_", " ")}</Badge>
                      </div>
                      <h2 className="mt-2 text-base font-semibold">{rec?.title}</h2>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold tabular-nums">{selected.score}</div>
                      <div className="text-[10px] text-muted-foreground">opportunity score</div>
                    </div>
                  </div>

                  {selected.page && <div className="break-all rounded-md border bg-muted/30 p-2 font-mono text-xs" data-testid="opportunity-affected-url">{selected.page}</div>}

                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Why this matters</h3>
                    <p className="mt-1 text-sm">{selected.why}</p>
                  </div>

                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Evidence</h3>
                    <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="opportunity-evidence">
                      {Object.entries(selected.evidence || {}).filter(([k, v]) => typeof v !== "object").map(([k, v]) => (
                        <div key={k} className="rounded-md border p-2">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.replaceAll("_", " ")}</div>
                          <div className="truncate text-sm font-semibold tabular-nums">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                        </div>
                      ))}
                    </div>
                    {selected.queries?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {selected.queries.map((q) => <Badge key={q} variant="outline" className="font-mono text-[10px]">{q}</Badge>)}
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Score breakdown</h3>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {Object.entries(selected.score_components || {}).map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[10px]">{k.replaceAll("_", " ")}: {v}</Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recommended actions</h3>
                    <ul className="mt-1 space-y-1">
                      {(rec?.recommended_actions || []).map((a) => (
                        <li key={a} className="flex items-start gap-1.5 text-sm"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{a}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="rounded-md border p-2">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Risk</div>
                      <div className="text-sm font-medium capitalize">{rec?.risk}</div>
                    </div>
                    <div className="rounded-md border p-2">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Expected direction</div>
                      <div className="text-sm">{rec?.expected_direction}</div>
                    </div>
                  </div>

                  <InfoNote>No numeric ranking is promised. Approval creates a scoped AI task; production still needs a second human approval.</InfoNote>

                  <div className="flex flex-wrap gap-2">
                    {selected.status === "open" && (
                      <>
                        <Button onClick={() => { setConfirmScope(false); setConfirmReview(false); setApproveOpen(true); }} data-testid="opportunity-approve-run-button">
                          <Play className="mr-1.5 h-4 w-4" /> Approve & Run
                        </Button>
                        <Button variant="outline" onClick={() => setStatus(selected, "saved")} data-testid="opportunity-save-button">
                          <Bookmark className="mr-1.5 h-4 w-4" /> Save
                        </Button>
                        <Button variant="outline" onClick={() => setStatus(selected, "dismissed")} data-testid="opportunity-dismiss-button">
                          <X className="mr-1.5 h-4 w-4" /> Dismiss
                        </Button>
                      </>
                    )}
                    {(selected.status === "dismissed" || selected.status === "saved") && (
                      <Button variant="outline" onClick={() => setStatus(selected, "open")} data-testid="opportunity-reopen-button">Reopen</Button>
                    )}
                    {selected.status === "in_progress" && selected.taskId && (
                      <Button variant="outline" onClick={() => navigate(`/dashboard/tasks/${selected.taskId}`)} data-testid="opportunity-view-task-button">View task</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-run-dialog">
          <DialogHeader>
            <DialogTitle>Approve & Run</DialogTitle>
            <DialogDescription>
              This creates a structured AI task with the evidence below. The AI may only make changes inside the approved scope.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Allowed changes</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(rec?.allowed_changes || []).map((c) => <Badge key={c} variant="outline" className="text-[10px]">{c.replaceAll("_", " ")}</Badge>)}
              </div>
              <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Protected areas</div>
              <div className="mt-1 text-xs text-muted-foreground">{(rec?.protected_areas || []).slice(0, 6).join(" · ")}</div>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={confirmScope} onCheckedChange={setConfirmScope} data-testid="approve-scope-checkbox" className="mt-0.5" />
              I approve this scoped change set for the affected page/queries.
            </label>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={confirmReview} onCheckedChange={setConfirmReview} data-testid="approve-review-checkbox" className="mt-0.5" />
              I understand I will review the result before anything reaches production.
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={approveRun} disabled={!confirmScope || !confirmReview || running} data-testid="approve-run-confirm-button">
              {running && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Approve & Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
